import { connectToSocket, getSocket } from "./socket/index.js";
import { setupLocalModeToggle } from "./vm/local-mode.js";
import { setupMetricsTracking } from "./vm/metrics.js";
import { setupVMOverrides, setupWorkspaceOverrides } from "./vm/overrides.js";
import { setupListeners } from "./socket/listeners.js";
import { setUserInfo, updateVersion, sendMessageToServer } from "./socket/events.js";
import { targetToName } from "./utils/scratch-helpers.js";
import { setVm, setStore, setBlId, setSocket } from "./socket/config.js";
import {
    findScratchBlocks,
    getGuiInternals,
    waitForBlockly,
} from "./utils/dom.js";
import {
    getRoomIdFromUrl,
    getNameFromUrl,
    getColorFromUrl,
    getAvatarFromUrl,
    generateRandomColor,
    generateUID,
} from "./utils/general.js";
import { DEFAULT_AVATAR_ID } from "../avatars/index.js";
import { setScratchBlocks, setBlocklyWorkspace } from "./blocks/index.js";
import { getReplayRoomId, initReplay } from "./replay-view.js";
import { startSyncWatchdog } from "./vm/resync.js";

// UI Components
import Chat from "./ui/chat.js";
import { addPlayerList } from "./ui/players.js";
import { lockRoomUI, unlockRoomUI } from "./ui/room-lock.js";
import { startDemoTimer } from "./ui/demo-timer.js";
import { lockProjectTitle } from "./ui/project-title.js";
import db from "../../components/landing-page/services/DatabaseManager.js";
import VoicePanel from "./ui/voice-panel.js";
import VoiceCall from "./voice/voice-call.js";

// Global state
let vm = null;
let store = null;
let blId = null;
let ScratchBlocks = null;
let blocklyWorkspace = null;

let username = "";
let uid = "";
let color = "";
let avatar = null;
let teacherAvatar = null;
let authUser = null;
let authToken = null;
let initialized = false;

let textChat = null;
let voiceCall = null;

let lastTitle;
let titleYetUnset = true;
setTimeout(() => {
    titleYetUnset = false;
}, 1000 * 2);

window.etuListeners = [];

/* ========================================================================== */
/* INITIALIZATION */
/* ========================================================================== */

export const init = async () => {
    const replayRoomId = getReplayRoomId();
    if (replayRoomId) return initReplay(replayRoomId);

    blId = getRoomIdFromUrl();
    const studentName = getNameFromUrl();
    username = studentName;

    const socket = await connectToSocket(blId);

    socket.on("connect", async () => {
        authUser = studentName ? null : await db.waitForAuthUser();
        authToken = authUser ? await db.getAuthToken() : null;

        await getSocket().emit(
            "checkUserInfo",
            { token: authToken, roomName: blId, username },
            async (valid) => {
                if (!valid) {
                    window.location.replace("/");
                }
            },
        );

        if (authUser) {
            const teacher = await db.getTeacherData(authUser.uid);
            username = teacher.name;
            teacherAvatar = teacher.avatar ?? null;
        }

        if (!initialized) {
            initialized = true;

            // Get React internals to access VM
            ({ vm, store } = await getGuiInternals());

            // Expose to config module
            setVm(vm);
            setStore(store);
            setBlId(blId);
            setSocket(socket);

            lastTitle = store.getState().scratchGui.projectTitle;
            lockProjectTitle();

            // Setup all modules
            setupMetricsTracking();
            setupVMOverrides();
            setupWorkspaceOverrides();
            setupLocalModeToggle();
            setupListeners(socket);
            startSyncWatchdog();

            // Blockly setup
            const workspace = await waitForBlockly();
            ScratchBlocks = findScratchBlocks();
            setScratchBlocks(ScratchBlocks);
            setBlocklyWorkspace(workspace);

            window.addEventListener("beforeunload", () => {
                updateVersion(vm);
            });

            onLoginUser();

            await applyRoomLock(blId, workspace);
            await applyDemoTimer(blId);
            return;
        }

        onLoginUser();
    });

    return socket;
};

const onLoginUser = async () => {
    if (!vm) return;

    const isTeacher = !!authUser;

    if (!uid) {
        color = getColorFromUrl() ?? generateRandomColor();
        uid = authUser?.uid ?? generateUID();
        avatar = teacherAvatar ?? getAvatarFromUrl() ?? DEFAULT_AVATAR_ID;
    }

    const json = vm.toJSON();
    setUserInfo(username, uid, color, avatar);

    if (!textChat) {
        addPlayerList();
        textChat = new Chat(getSocket(), username, color, avatar, uid);
    }

    if (!voiceCall) {
        voiceCall = new VoiceCall(getSocket(), { isTeacher });
        new VoicePanel(voiceCall, { username, color, avatar, isTeacher });
    }

    let target = vm.editingTarget;

    getSocket().emit("join", {
        id: blId,
        username,
        uid,
        color,
        avatar,
        json,
        token: authToken,
    });

    const maxAttempts = 50;
    let attempts = 0;

    const interval = setInterval(() => {
        const target = vm.editingTarget;

        if (target) {
            clearInterval(interval);
            getSocket().emit("updateFirstSprite", {
                target: JSON.stringify(target),
            });
            sendMessageToServer("spriteFocus", { target: targetToName(target) });
            return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
            clearInterval(interval);
            console.warn("vm.editingTarget no disponible tras 5s");
        }
    }, 100);
};

const applyDemoTimer = async (blId) => {
    try {
        const room = await db.getRoom(blId);
        if (room?.isDemo) startDemoTimer(room.expiresAt);
    } catch (e) {
        console.error("Error reading the room's expiry:", e);
    }
};

const applyRoomLock = async (blId, workspace) => {
    try {
        const locked = await db.roomLocked(blId);

        if (locked) lockRoomUI(workspace);
        else unlockRoomUI(workspace);
    } catch (e) {
        throw new Error(e);
    }
};

const removeWorkspaceOverlay = () => {
    const style = document.getElementById("scratch-room-lock-overlay");
    if (style) style.remove();
    document.querySelectorAll('[data-_room-locked="true"]').forEach((el) => {
        el.style.display = "";
        delete el.dataset._roomLocked;
    });
};

/* ========================================================================== */
/* EXPORTS */
/* ========================================================================== */

export {
    vm,
    store,
    blId,
    ScratchBlocks,
    blocklyWorkspace,
    username,
    uid,
    color,
    avatar,
    textChat,
    lastTitle,
    titleYetUnset,
};

export { getSocket };
